/* Source: /tmp/jacbuilder-deploy-scratch/deploy-8asygg26/features/map/IncidentMap.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { Map as MapLibreMap, Marker, NavigationControl, AttributionControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { feature } from "topojson-client";
import { cn } from "../../lib/utils.js";
import { density_geojson } from "./Density.js";
import { linked_people, pin_status, pin_label, pin_dot } from "./Status.js";
import { __jacCallFunction } from "@jac/runtime";
import { useRef } from "react";
import { useState } from "@jac/runtime";
import { useEffect } from "@jac/runtime";
import { _jac } from "@jac/prelude";
async function get_boundaries() {
  return await __jacCallFunction("get_boundaries", {});
}
async function get_bhotekoshi_corridor() {
  return await __jacCallFunction("get_bhotekoshi_corridor", {});
}
async function get_nepal_rivers() {
  return await __jacCallFunction("get_nepal_rivers", {});
}
let INCIDENT_DISTRICT_ID = 30;
function css_var(name, opacity = (-1.0)) {
  let styles = getComputedStyle(document.documentElement);
  let raw = styles.getPropertyValue(name).trim();
  let canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  let ctx = canvas.getContext("2d");
  ctx.fillStyle = raw;
  ctx.fillRect(0, 0, 1, 1);
  let px = ctx.getImageData(0, 0, 1, 1).data;
  return (((((((("rgba(" + String(px[0])) + ",") + String(px[1])) + ",") + String(px[2])) + ",") + String(((opacity >= 0) ? opacity : (px[3] / 255)))) + ")");
}
function blank_style() {
  return {"version": 8, "sources": {}, "layers": [{"id": "bg", "type": "background", "paint": {"background-color": css_var("--map-surface")}}]};
}
function add_boundary_layers(map_obj, topo) {
  let districts = feature(topo, topo.objects.districts);
  let provinces = feature(topo, topo.objects.provinces);
  map_obj.addSource("districts", {"type": "geojson", "data": districts});
  map_obj.addSource("provinces", {"type": "geojson", "data": provinces});
  map_obj.addLayer({"id": "district-fill", "type": "fill", "source": "districts", "paint": {"fill-color": ["case", ["==", ["get", "district_id"], INCIDENT_DISTRICT_ID], css_var("--map-land"), css_var("--map-land")], "fill-opacity": ["case", ["==", ["get", "district_id"], INCIDENT_DISTRICT_ID], 0.6, 1]}});
  map_obj.addLayer({"id": "district-line", "type": "line", "source": "districts", "paint": {"line-color": ["case", ["==", ["get", "district_id"], INCIDENT_DISTRICT_ID], css_var("--primary"), css_var("--map-grid")], "line-width": 0.8, "line-opacity": 0.6}});
  map_obj.addLayer({"id": "province-line", "type": "line", "source": "provinces", "paint": {"line-color": css_var("--muted-foreground"), "line-width": 1.2, "line-opacity": 0.6}});
  let above = first_layer(map_obj, ["nepal-rivers", "report-heat", "corridor-band"]);
  if (above) {
    for (const layer of ["district-fill", "district-line", "province-line"]) {
      map_obj.moveLayer(layer, above);
    }
  }
}
function first_layer(map_obj, ids) {
  for (const layer_id of ids) {
    if (map_obj.getLayer(layer_id)) {
      return layer_id;
    }
  }
  return null;
}
function add_river_layers(map_obj, rivers) {
  map_obj.addSource("nepal-rivers", {"type": "geojson", "data": rivers, "attribution": "\u00a9 <a href='https://www.openstreetmap.org/copyright' target='_blank' rel='noopener noreferrer'>OpenStreetMap contributors</a> (ODbL)"});
  map_obj.addLayer({"id": "nepal-rivers", "type": "line", "source": "nepal-rivers", "layout": {"line-cap": "round", "line-join": "round"}, "paint": {"line-color": css_var("--map-water", 1.0), "line-opacity": 0.55, "line-width": ["interpolate", ["linear"], ["zoom"], 6, 0.6, 10, 1.2, 14, 2]}}, first_layer(map_obj, ["report-heat", "corridor-band"]));
}
function add_corridor_layers(map_obj, corridor) {
  map_obj.addSource("bhotekoshi-corridor", {"type": "geojson", "data": corridor, "attribution": "\u00a9 <a href='https://www.openstreetmap.org/copyright' target='_blank' rel='noopener noreferrer'>OpenStreetMap contributors</a> (ODbL)"});
  map_obj.addLayer({"id": "corridor-band", "type": "line", "source": "bhotekoshi-corridor", "filter": ["==", ["geometry-type"], "LineString"], "layout": {"line-cap": "round", "line-join": "round"}, "paint": {"line-color": css_var("--status-warning", 1.0), "line-opacity": 0.13, "line-width": ["interpolate", ["linear"], ["zoom"], 8, 10, 12, 28, 16, 52]}});
  map_obj.addLayer({"id": "corridor-river", "type": "line", "source": "bhotekoshi-corridor", "filter": ["==", ["geometry-type"], "LineString"], "layout": {"line-cap": "round", "line-join": "round"}, "paint": {"line-color": css_var("--status-warning", 1.0), "line-opacity": 0.95, "line-width": ["interpolate", ["linear"], ["zoom"], 8, 1.5, 12, 3.5, 16, 6]}});
  if (map_obj.getLayer("report-heat")) {
    map_obj.moveLayer("report-heat");
  }
  if (map_obj.getLayer("report-circles")) {
    map_obj.moveLayer("report-circles");
  }
  let labels = [];
  for (const item of corridor.features) {
    if ((item.geometry.type === "Point")) {
      let label = document.createElement("span");
      label.className = "pointer-events-none rounded bg-card/90 px-1.5 py-0.5 text-xs font-medium text-foreground shadow-sm";
      label.textContent = item.properties.label;
      label.setAttribute("data-corridor-settlement", item.properties.label);
      let marker = Reflect.construct(Marker, [{"element": label, "anchor": "bottom-left", "offset": [8, (-8)]}]);
      marker.setLngLat(item.geometry.coordinates).addTo(map_obj);
      labels.push(marker);
    }
  }
  return labels;
}
function update_density(map_obj, groups) {
  let data = density_geojson(groups);
  let maximum = Math.max([...[2], ...groups.map(g => g.report_count)]);
  let weight = ["interpolate", ["linear"], ["get", "report_count"], 0, 0, 1, 0.05, maximum, 1];
  if (map_obj.getSource("report-counts")) {
    map_obj.getSource("report-counts").setData(data);
    map_obj.setPaintProperty("report-heat", "heatmap-weight", weight);
    return;
  }
  map_obj.addSource("report-counts", {"type": "geojson", "data": data});
  map_obj.addLayer({"id": "report-heat", "type": "heatmap", "source": "report-counts", "paint": {"heatmap-weight": weight, "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 8, 2.5, 12, 3, 16, 3], "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 8, 32, 12, 60, 16, 80], "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.85, 13, 0.6, 16, 0], "heatmap-color": ["interpolate", ["linear"], ["heatmap-density"], 0, css_var("--primary", 0.0), 0.2, css_var("--map-water"), 0.4, css_var("--primary"), 0.7, css_var("--status-warning"), 1, css_var("--status-missing")]}});
  map_obj.addLayer({"id": "report-circles", "type": "circle", "source": "report-counts", "minzoom": 11, "paint": {"circle-color": css_var("--primary"), "circle-opacity": 0.75, "circle-stroke-color": css_var("--background"), "circle-stroke-width": 2, "circle-radius": ["interpolate", ["linear"], ["get", "report_count"], 1, 7, 500, 20, 1500, 30]}});
}
function make_pin(label, location_id, status, on_select, label_left = false) {
  let el = document.createElement("button");
  el.type = "button";
  el.setAttribute("aria-label", ((("Inspect " + label) + " \u00b7 ") + pin_label(status)));
  el.addEventListener("click", () => on_select(location_id));
  el.className = "flex cursor-pointer flex-row items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  let dot = document.createElement("span");
  dot.className = ("block size-3.5 rounded-full border-2 border-background shadow " + pin_dot(status));
  let tag = document.createElement("span");
  tag.className = "whitespace-nowrap rounded border border-border bg-card/95 px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-sm";
  tag.textContent = label;
  if (label_left) {
    el.appendChild(tag);
    el.appendChild(dot);
  } else {
    el.appendChild(dot);
    el.appendChild(tag);
  }
  return el;
}
function IncidentMap(props) {
  const {locations, people, active: active = true, className: className = "", focusLocationId: focusLocationId = "", focusRequest: focusRequest = 0, onSelect: onSelect = null, reportGroups: reportGroups = [], onSelectSettlement: onSelectSettlement = null, onCorridorReady: onCorridorReady = null} = props;
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const settlementMarkersRef = useRef([]);
  const [corridorStatus, setCorridorStatus] = useState("loading");
  const corridorTimerRef = useRef(null);
  const onSelectRef = useRef(null);
  const onSettlementRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [boundaryStatus, setBoundaryStatus] = useState("loading");
  const boundaryTimerRef = useRef(null);
  const resizeRef = useRef(null);
  const lastFocusRequest = useRef(0);
  function select_location(location_id) {
    if (onSelectRef.current) {
      onSelectRef.current(location_id);
    }
  }
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    onSettlementRef.current = onSelectSettlement;
  }, [onSelectSettlement]);
  useEffect(() => {
    if ((mapRef.current || !containerRef.current)) {
      return;
    }
    try {
      let map_obj = Reflect.construct(MapLibreMap, [{"container": containerRef.current, "style": blank_style(), "bounds": [[85.82, 27.74], [86.00, 28.06]], "fitBoundsOptions": {"padding": 42}, "minZoom": 6, "maxZoom": 14, "maxBounds": [[79.0, 25.5], [89.5, 31.2]], "renderWorldCopies": false, "attributionControl": false}]);
      mapRef.current = map_obj;
      map_obj.addControl(Reflect.construct(NavigationControl, [{"showCompass": false}]), "top-left");
      map_obj.addControl(Reflect.construct(AttributionControl, [{"compact": true, "customAttribution": "\u00a9 <a href='https://www.openstreetmap.org/copyright' target='_blank' rel='noopener noreferrer'>OpenStreetMap contributors</a> (ODbL)"}]), "bottom-right");
      map_obj.on("load", () => {
        if ((mapRef.current !== map_obj)) {
          return;
        }
        setStatus("ready");
        load_boundaries(map_obj);
        load_rivers(map_obj);
        load_corridor(map_obj);
        map_obj.on("click", "report-circles", event => {
          if (((event.features && (_jac.builtin.len(event.features) > 0)) && onSettlementRef.current)) {
            onSettlementRef.current.call(null, event.features[0].properties.key);
          }
        });
        map_obj.on("mouseenter", "report-circles", () => {
          map_obj.getCanvas().style.cursor = "pointer";
        });
        map_obj.on("mouseleave", "report-circles", () => {
          map_obj.getCanvas().style.cursor = "";
        });
      });
      let observer = Reflect.construct(ResizeObserver, [() => {
  if (mapRef.current) {
    mapRef.current.resize();
  }
}]);
      resizeRef.current = observer;
      observer.observe(containerRef.current);
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setStatus("error");
        setErrorText(String(e));
      } else {
        throw __jac_e;
      }
    }
  }, []);
  async function load_boundaries(map_obj) {
    boundaryTimerRef.current = setTimeout(() => {
      if ((mapRef.current === map_obj)) {
        setBoundaryStatus("unavailable");
      }
    }, 5000);
    try {
      let topo = await __jacCallFunction("get_boundaries", {});
      if ((mapRef.current !== map_obj)) {
        return;
      }
      if (topo["error"]) {
        setBoundaryStatus("unavailable");
      } else {
        add_boundary_layers(map_obj, topo);
        setBoundaryStatus("ready");
      }
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        if ((mapRef.current === map_obj)) {
          setBoundaryStatus("unavailable");
        }
      } else {
        throw __jac_e;
      }
    } finally {
      if (boundaryTimerRef.current) {
        clearTimeout(boundaryTimerRef.current);
      }
    }
  }
  async function load_rivers(map_obj) {
    try {
      let rivers = await __jacCallFunction("get_nepal_rivers", {});
      if (((mapRef.current === map_obj) && !rivers["error"])) {
        add_river_layers(map_obj, rivers);
      }
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        return;
      } else {
        throw __jac_e;
      }
    }
  }
  async function load_corridor(map_obj) {
    corridorTimerRef.current = setTimeout(() => {
      if ((mapRef.current === map_obj)) {
        setCorridorStatus("unavailable");
      }
    }, 5000);
    try {
      let corridor = await __jacCallFunction("get_bhotekoshi_corridor", {});
      if ((mapRef.current !== map_obj)) {
        return;
      }
      if (corridor["error"]) {
        setCorridorStatus("unavailable");
      } else {
        settlementMarkersRef.current = add_corridor_layers(map_obj, corridor);
        setCorridorStatus("ready");
        if (onCorridorReady) {
          onCorridorReady.call(null, corridor.features);
        }
      }
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        if ((mapRef.current === map_obj)) {
          setCorridorStatus("unavailable");
        }
      } else {
        throw __jac_e;
      }
    } finally {
      if (corridorTimerRef.current) {
        clearTimeout(corridorTimerRef.current);
      }
    }
  }
  useEffect(() => {
    let loc;
    let label_left;
    let map_obj = mapRef.current;
    if ((!map_obj || (status !== "ready"))) {
      return;
    }
    for (const m of markersRef.current) {
      _jac.poly.call(m, "remove");
    }
    let fresh = [];
    let pin_places = (((locations.length <= 20) || (reportGroups.length === 0)) ? locations.slice(0, 20) : []);
    for (const index of _jac.builtin.range(pin_places.length)) {
      loc = pin_places.at(index);
      label_left = (_jac.int.mod(index, 2) === 1);
      let marker = Reflect.construct(Marker, [{"element": make_pin(loc.name, loc.id, pin_status(linked_people(loc, people)), select_location, label_left), "anchor": (label_left ? "right" : "left"), "offset": [(label_left ? 7 : (-7)), 0]}]);
      marker.setLngLat([loc.longitude, loc.latitude]).addTo(map_obj);
      fresh.push(marker);
    }
    markersRef.current = fresh;
  }, [locations, people, reportGroups, status]);
  useEffect(() => {
    if ((mapRef.current && (status === "ready"))) {
      update_density(mapRef.current, reportGroups);
    }
  }, [reportGroups, status]);
  useEffect(() => {
    if (((active && (status === "ready")) && mapRef.current)) {
      mapRef.current.resize();
    }
  }, [active, status]);
  useEffect(() => {
    if (((!active || (status !== "ready")) || !mapRef.current)) {
      return;
    }
    if ((focusRequest <= lastFocusRequest.current)) {
      return;
    }
    let places = locations.filter(p => (p.id === focusLocationId)).map(p => p);
    if (!_jac.builtin.bool(places)) {
      return;
    }
    mapRef.current.resize();
    mapRef.current.flyTo({"center": [places[0].longitude, places[0].latitude], "zoom": Math.max(mapRef.current.getZoom(), 13), "duration": 1400});
    lastFocusRequest.current = focusRequest;
  }, [focusLocationId, focusRequest, active, status]);
  useEffect(() => {
    return () => {
      if (boundaryTimerRef.current) {
        clearTimeout(boundaryTimerRef.current);
      }
      if (corridorTimerRef.current) {
        clearTimeout(corridorTimerRef.current);
      }
      for (const marker of settlementMarkersRef.current) {
        _jac.poly.call(marker, "remove");
      }
      if (resizeRef.current) {
        resizeRef.current.disconnect();
      }
      if (mapRef.current) {
        _jac.poly.call(mapRef.current, "remove");
        mapRef.current = null;
      }
    };
  }, []);
  return __jacJsx("div", {"className": cn("relative h-full w-full overflow-hidden rounded-md border border-border bg-map-surface", className)}, [__jacJsx("div", {"ref": containerRef, "className": "h-full min-h-80 w-full"}, []), ((status === "loading") && __jacJsx("div", {"className": "absolute inset-0 flex items-center justify-center text-sm text-muted-foreground"}, ["Loading river corridor map..."])), (((status === "ready") && (boundaryStatus !== "ready")) && __jacJsx("p", {"role": "status", "className": "pointer-events-none absolute bottom-2 left-2 right-2 rounded border border-border bg-card/95 p-2 text-center text-xs text-muted-foreground"}, [((boundaryStatus === "unavailable") ? "Boundaries unavailable. Pins, place list, and linked evidence remain available." : "Loading district boundaries; pins and evidence are ready.")])), (((status === "ready") && (corridorStatus !== "ready")) && __jacJsx("p", {"role": "status", "className": "pointer-events-none absolute top-2 left-12 right-2 rounded border border-border bg-card/95 p-2 text-center text-xs text-muted-foreground"}, [((corridorStatus === "unavailable") ? "River corridor unavailable; reported places and evidence remain usable." : "Loading current river linework...")])), ((status === "error") && __jacJsx("div", {"className": "absolute inset-0 flex items-center justify-center pl-6 pr-6 text-center text-sm text-destructive"}, [("Map unavailable: " + errorText)]))]);
}
/*jac:refresh-boundary*/;
export {IncidentMap};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:get_boundaries": {"is_writer": false, "touches": ["*"]}, "func:get_bhotekoshi_corridor": {"is_writer": false, "touches": ["*"]}, "func:get_nepal_rivers": {"is_writer": false, "touches": ["*"]}}); };
//# sourceMappingURL=IncidentMap.js.map
